import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import TaskActionRunner from 'app/shared/modules/TaskActionRunner';
import TaskInfo from 'app/shared/modules/TaskInfo';
import SlaGroupInfoTable from 'app/shared/modules/TaskSlaGroupInfoTable';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useSort from 'hooks/useSort';

import { openDialogAction } from 'store/global/actions';

import { getLoanConditionTasksTableConfig } from './LoanConditionTasksTable.config';
import { LoanConditionTasksTableTestIds } from './LoanConditionTasksTable.testIds';

const defaultArray = [];
const defaultObject = {};

const LoanConditionTasksTable = ({
  tasks = defaultArray,
  tasksActions = defaultObject,
  formName,
  conditionId,
  taskActionRunnerContext = null,
  onTaskActionRefresh = null,
}) => {
  const dispatch = useDispatch();

  const { order, onRequestSort } = useSort();

  const orderedTasks = useMemo(
    () =>
      _.orderBy(tasks, [task => _.toLower(task[order.id])], [order.direction])
        .filter(Boolean)
        .map(task => ({ ...task, id: task.taskId })),
    [order, tasks],
  );

  const onOpenTaskSLAGroupInfo = useCallback(
    ({ slaGroupId, taskId, name }) => {
      dispatch(
        openDialogAction({
          size: ModalSize.LARGE,
          header: `${name} SLA Group Info`,
          body: dialogProps => (
            <SlaGroupInfoTable slaGroupId={slaGroupId} taskId={taskId} {...dialogProps} />
          ),
        }),
      );
    },
    [dispatch],
  );

  const onOpenTaskInfo = useCallback(
    taskId => {
      const task = orderedTasks.find(({ id }) => id === taskId);

      dispatch(
        openDialogAction({
          header: 'Task Info',
          body: dialogProps => (
            <TaskActionRunner
              runnerContext={taskActionRunnerContext}
              task={taskActionRunnerContext ? task : null}
              onTaskRefresh={onTaskActionRefresh}
            >
              <TaskInfo
                {...dialogProps}
                conditionId={conditionId}
                formName={formName}
                task={task}
                {...tasksActions}
              />
            </TaskActionRunner>
          ),
        }),
      );
    },
    [
      dispatch,
      orderedTasks,
      conditionId,
      formName,
      tasksActions,
      taskActionRunnerContext,
      onTaskActionRefresh,
    ],
  );

  const tasksTableConfig = useMemo(
    () => getLoanConditionTasksTableConfig({ onOpenTaskSLAGroupInfo }),
    [onOpenTaskSLAGroupInfo],
  );

  return (
    <Box data-testid={LoanConditionTasksTableTestIds.LOAN_CONDITION_SLA_GROUP_TABLE}>
      <TableCDS
        isStickyHeader
        config={tasksTableConfig}
        data={orderedTasks}
        order={order}
        onRequestSort={onRequestSort}
        onRowClick={onOpenTaskInfo}
      />
    </Box>
  );
};

LoanConditionTasksTable.propTypes = {
  conditionId: PropTypes.string.isRequired,
  tasks: PropTypes.arrayOf(PropTypes.object),
  tasksActions: PropTypes.object,
  formName: PropTypes.string,
  taskActionRunnerContext: PropTypes.object,
  onTaskActionRefresh: PropTypes.func,
};

export default memo(LoanConditionTasksTable);
